import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { spanText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { CheckIcon } from '../../shared/ui/icons';
import { Meter } from '../../shared/ui/meter';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { isFinished, stopBulkAfterBatch, useBulkOperation } from '../../entities/bulk-operation';
import { previewIssueRedeliver, startIssueRetry, type TIssueDetail } from '../../entities/issue';

// The job runner's own batch size (flows-api §9); the screen only explains it.
const BATCH = 500;

const batchesOf = (total: number): number[] => {
  const sizes: number[] = [];
  for (let left = total; left > 0; left -= BATCH) sizes.push(Math.min(BATCH, left));
  return sizes;
};

const count = (value: number) => value.toLocaleString('en-US');

interface IIssueRedeliverProps {
  issue: TIssueDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 429 on this step: resending into the same limit fails again, so the dialog says so. */
  rateLimited: boolean;
  /** A 4xx: refused as sent, so resending the same body is expected to fail the same way. */
  refused?: boolean;
  destinationName: string | null;
  /** "on v4": the version the copies started on, when the step is gone from the live one. */
  onVersion: number | null;
}

// Board 19 "Redeliver · at most 500 per batch": a server job, so the page can be left.
export const IssueRedeliver = ({
  issue,
  open,
  onOpenChange,
  rateLimited,
  refused = false,
  destinationName,
  onVersion,
}: IIssueRedeliverProps) => {
  const client = useQueryClient();
  const [operationId, setOperationId] = useState<string | null>(null);
  const idempotencyKey = useMemo(() => (open ? crypto.randomUUID() : ''), [open]);

  const preview = useQuery({
    queryKey: ['issue-redeliver-preview', issue.id],
    queryFn: () => previewIssueRedeliver(issue),
    enabled: open && operationId === null,
  });
  const live = useBulkOperation(operationId);
  const operation = live.operation;

  const start = useMutation({
    mutationFn: () => startIssueRetry(issue.id, idempotencyKey),
    onSuccess: (job) => setOperationId(job.id),
  });
  const stop = useMutation({ mutationFn: () => stopBulkAfterBatch(operationId as string) });

  const close = (next: boolean) => {
    if (!next) {
      setOperationId(null);
      start.reset();
      stop.reset();
      void client.invalidateQueries({ queryKey: ['issue', issue.id] });
      void client.invalidateQueries({ queryKey: ['issue-runs', issue.id] });
      void client.invalidateQueries({ queryKey: ['issue-redeliver-preview', issue.id] });
      void client.invalidateQueries({ queryKey: ['issues'] });
    }
    onOpenChange(next);
  };

  const where = onVersion === null ? '' : ` on v${onVersion}`;

  if (operation && isFinished(operation)) {
    const again = operation.failed;
    return (
      <Modal
        open={open}
        onOpenChange={close}
        title={`${count(operation.succeeded)} sent again${again > 0 ? `, ${count(again)} could not be sent` : ''}`}
        description={[
          operation.stopAfterBatch || operation.state === 'cancelled'
            ? `Stopped after the batch that was running: ${count(Math.max(0, (operation.total ?? 0) - operation.processed))} were not sent.`
            : null,
          again > 0 ? `The ${count(again)} that could not be sent stay in this issue.` : null,
          'A copy sent again that fails again lands back in this issue, with its answer.',
        ]
          .filter(Boolean)
          .join(' ')}
        footer={
          <Button size="sm" variant="secondary" onClick={() => close(false)}>
            Done
          </Button>
        }
      >
        {operation.error && <Note tone="danger">{operation.error}</Note>}
      </Modal>
    );
  }

  if (operationId) {
    const total = operation?.total ?? 0;
    const processed = operation?.processed ?? 0;
    const batches = batchesOf(total);
    const current = Math.min(batches.length, Math.floor(processed / BATCH) + 1);
    return (
      <Modal
        open={open}
        onOpenChange={close}
        title={batches.length > 0 ? `Batch ${current} of ${batches.length}` : 'Starting'}
        description={
          <span className="font-mono text-micro text-faint">
            {operationId} · runs on the server
            <br />
            safe to leave this page — it goes on without it
          </span>
        }
        footer={
          <Button
            size="sm"
            variant="ghost"
            className="text-danger"
            disabled={operation?.stopAfterBatch}
            waiting={stop.isPending}
            waitingLabel="Stopping"
            onClick={() => stop.mutate()}
          >
            {operation?.stopAfterBatch ? 'Stops after this batch' : 'Stop after this batch'}
          </Button>
        }
      >
        {!operation && <LoadingState rows={3} />}
        <div className="flex flex-col gap-3">
          {batches.map((size, index) => {
            const done = Math.min(size, Math.max(0, processed - index * BATCH));
            return (
              <div key={index}>
                <div className="flex items-center justify-between text-meta">
                  <span className="text-muted">Batch {index + 1}</span>
                  <span className="flex items-center gap-2 font-mono text-primary">
                    {done === size ? (
                      <>
                        <CheckIcon size={11} className="text-success" /> {count(size)} sent
                      </>
                    ) : done > 0 ? (
                      `${count(done)} of ${count(size)}`
                    ) : (
                      <span className="text-faint">waiting</span>
                    )}
                  </span>
                </div>
                <Meter className="mt-2" value={(done / size) * 100} tone="success" />
              </div>
            );
          })}
        </div>
        {operation && (
          <p className="mt-3 font-mono text-micro text-faint">
            {count(operation.succeeded)} sent again · {count(operation.failed)} could not be sent
            {operation.etaMs ? ` · about ${spanText(operation.etaMs)} left` : ''}
          </p>
        )}
        {stop.isError && <MutationError error={stop.error} />}
      </Modal>
    );
  }

  const total = preview.data?.total ?? 0;
  const batches = batchesOf(total);
  return (
    <Modal
      open={open}
      onOpenChange={close}
      title={
        preview.data
          ? `Redeliver ${count(total)} ${total === 1 ? 'copy' : 'copies'}${where}`
          : 'Redeliver'
      }
      description={
        preview.data && total > 0
          ? `A server job sends them in ${batches.length} ${batches.length === 1 ? 'batch' : 'batches'} of up to ${BATCH}${batches.length > 1 ? ', one after another' : ''}.${
              rateLimited
                ? ` ${destinationName ?? 'The destination'} is refusing with 429 — cap its rate first, or most of these fail again.`
                : refused
                  ? ` ${destinationName ?? 'The destination'} refused these as sent (4xx): the same body gets the same answer unless the flow or the endpoint changed since.`
                  : ''
            }`
          : undefined
      }
      submit={{
        label:
          batches.length > 1
            ? `Redeliver in ${batches.length} batches`
            : `Redeliver ${count(total)}`,
        onSubmit: () => start.mutate(),
        disabled: !preview.data || total === 0 || start.isPending,
        waiting: start.isPending,
        waitingLabel: 'Starting',
      }}
    >
      {preview.isError && (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      )}
      {preview.isPending && <LoadingState rows={2} />}
      {preview.data && total === 0 && (
        <Note>
          No failed copy of this step is left to redeliver: each one was redelivered already or
          passed the retention horizon.
        </Note>
      )}
      {preview.data && total > 0 && (
        <div className="flex flex-col gap-2 text-meta">
          <div className="flex justify-between">
            <span className="text-muted">Batches</span>
            <span className="font-mono text-primary">{batches.map(count).join(' · ')}</span>
          </div>
          {preview.data.notes.map((note) => (
            <Note key={note}>{note}</Note>
          ))}
        </div>
      )}
      {start.isError && (
        <div className="mt-3">
          <MutationError error={start.error} />
        </div>
      )}
    </Modal>
  );
};
